/**
 * useJoinPool Hook - Phase 2C-2
 * 
 * Mutation hook for joining a pool.
 * Uses existing PoolService pattern.
 */

import { useState, useCallback } from 'react';
import { PoolMembership } from '../types';
import { PoolService, ApiError } from '../api';

interface UseJoinPoolResult {
  joinPool: (poolId: string) => Promise<PoolMembership | null>;
  loading: boolean;
  error: string | null;
  success: boolean;
  reset: () => void;
}

export function useJoinPool(): UseJoinPoolResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const joinPool = useCallback(async (poolId: string): Promise<PoolMembership | null> => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      const membership = await PoolService.join(poolId);
      setSuccess(true);
      return membership;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to join pool';
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setError(null);
    setSuccess(false);
  }, []);

  return { joinPool, loading, error, success, reset };
}
